import type { ToolEffectHandler } from '../types'
import { TOOL_NAMES } from '@/components/panels/chat/tool-renderers/constants'

/**
 * Place search result data structure (matches Go backend PlaceSearchResult).
 * Note: PlaceSummary does not include lat/lng coordinates.
 * For full coordinates, use PlaceDetails which includes LocationInfo.
 */
interface PlaceSearchResult {
  description: string
  places: PlaceSummary[]
}

interface PlaceSummary {
  placeId: string
  name: string
  types: string[]
  address: string
  rating: number
  reviewCount: number
  priceLevel: number
  description: string
  distance: number
  provider: string
}

/**
 * Effect handler for place search tool results.
 * Currently logs place results for debugging.
 *
 * TODO: Implement map zooming when coordinates become available.
 * Current limitation: PlaceSummary does not include lat/lng coordinates.
 * Possible solutions:
 * 1. Update backend to include coordinates in PlaceSummary
 * 2. Use Google Maps Places API to geocode place IDs
 * 3. Use Google Maps Geocoding API to geocode addresses
 * 4. Fetch PlaceDetails for each place (includes LocationInfo with lat/lng)
 *
 * Once coordinates are available, this handler should:
 * - Create a LatLngBounds from all place coordinates
 * - Call mapControls.fitBounds(bounds, true) to animate map
 * - Optionally add place markers to the map
 */
const handlePlaceResult: ToolEffectHandler<PlaceSearchResult> = (
  result,
  context
) => {
  const { mapControls } = context

  if (!mapControls) {
    console.debug(
      '[tool-effects/place] Map controls not available, skipping place effect'
    )
    return
  }

  if (!result.places || result.places.length === 0) {
    console.debug(
      '[tool-effects/place] No places in result, skipping map interaction'
    )
    return
  }

  // Log place information for debugging
  console.log('[tool-effects/place] Received place search results:', {
    count: result.places.length,
    places: result.places.map((p) => ({
      name: p.name,
      placeId: p.placeId,
      address: p.address,
      provider: p.provider,
    })),
  })

  // TODO: Implement map zooming once coordinates are available
  // Example implementation (when coordinates exist):
  /*
  try {
    const bounds = new google.maps.LatLngBounds()

    result.places.forEach(place => {
      if (place.latitude && place.longitude) {
        bounds.extend({
          lat: place.latitude,
          lng: place.longitude
        })
      }
    })

    if (!bounds.isEmpty()) {
      mapControls.fitBounds(bounds, true)
    }
  } catch (error) {
    console.error('[tool-effects/place] Failed to zoom map:', error)
  }
  */
}

/**
 * Export registry mapping tool name to handler.
 * This will be auto-imported by the main tool effects registry.
 */
export default {
  [TOOL_NAMES.PLACE]: handlePlaceResult,
}
